import { useState } from 'react';
import { isEmpty } from 'lodash';
import { notify, useTrackWithUser } from '@ion/api';
import uploadAnalyticsFile from './uploadAnalyticsFile';
import { DEFAULT_UPDATE_MODE } from 'app/components/analytics-builder/export-import/UPDATE_MODES';

const YAML_FILE_EXTENSION_REGEX = /\.ya?ml$/;

/**
 * Custom hook to handle Analytics File upload functionality
 * Used internally by AjsFileUploadModal
 *
 * @param {object} params - Hook parameters
 * @param {function} params.setField - Form setField function
 * @param {function} params.addIdentitySyncs - Function to add identity syncs to staged list
 * @param {function} params.setStagedIntegrations - Function to set staged integrations
 * @param {array} params.stagedIdentitySyncs - Current staged identity syncs (for merge mode)
 * @param {array} params.stagedIntegrations - Current staged integrations (for merge mode)
 * @param {function} params.onSuccess - Callback to run on successful upload
 * @returns {object} Hook interface
 */
export default function useAnalyticsFileUpload({
  setField,
  addIdentitySyncs,
  setStagedIntegrations,
  stagedIdentitySyncs,
  stagedIntegrations,
  onSuccess,
}) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(Date.now());
  const [updateMode, setUpdateMode] = useState(DEFAULT_UPDATE_MODE);
  const [trackUpload] = useTrackWithUser('analytics_file_uploaded');

  const resetFileInput = () => {
    setSelectedFile(null);
    setFileInputKey(Date.now());
    setUpdateMode(DEFAULT_UPDATE_MODE);
  };

  const handleModeChange = event => {
    setUpdateMode(event.target.value);
  };

  const handleFileChange = event => {
    const file = event.target.files[0];
    const isYamlFile = file && YAML_FILE_EXTENSION_REGEX.test(file.name);
    setSelectedFile(isYamlFile ? file : null);
  };

  const processFileContent = buildProcessFileContent({
    setField,
    addIdentitySyncs,
    setStagedIntegrations,
    updateMode,
    stagedIdentitySyncs,
    stagedIntegrations,
    resetFileInput,
    onSuccess,
    trackUpload,
  });

  const handleConfirm = buildHandleConfirm({ selectedFile, processFileContent });

  return {
    selectedFile,
    fileInputKey,
    updateMode,
    handleFileChange,
    handleModeChange,
    handleConfirm,
    resetFileInput,
  };
}

/**
 * Build the file content processor function
 *
 * @param {object} params - Parameters
 * @returns {function} File content processor function
 */
function buildProcessFileContent({
  setField,
  addIdentitySyncs,
  setStagedIntegrations,
  updateMode,
  stagedIdentitySyncs,
  stagedIntegrations,
  resetFileInput,
  onSuccess,
  trackUpload,
}) {
  return fileContent => {
    const addIntegrations = integrationNames => {
      setStagedIntegrations(integrationNames);
    };

    const { success, errors } = uploadAnalyticsFile({
      fileContent,
      setField,
      addIdentitySyncs,
      addIntegrations,
      updateMode,
      stagedIdentitySyncs,
      stagedIntegrations,
    });

    if (success) {
      trackUpload();
      notify.success({ message: 'Analytics File uploaded successfully' });
      resetFileInput();
      onSuccess();
    } else {
      const errorMessage = isEmpty(errors) ? 'Failed to upload Analytics File' : errors.join(', ');
      notify.error({ message: errorMessage });
    }
  };
}

/**
 * Build the confirm handler function
 *
 * @param {object} params - Parameters
 * @returns {function} Confirm handler function
 */
function buildHandleConfirm({ selectedFile, processFileContent }) {
  return () => {
    if (!selectedFile) {
      return;
    }

    const reader = new FileReader();
    reader.onload = event => processFileContent(event.target.result);
    reader.onerror = () => notify.error({ message: 'Failed to read file' });
    reader.readAsText(selectedFile);
  };
}
